import { useEffect, useState } from 'react';
import API from '../api/api';
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SavingsIcon from '@mui/icons-material/Savings';
import InventoryIcon from '@mui/icons-material/Inventory2';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useSnackbar } from '../contexts/SnackbarContext';

const formatCurrency = (value) => {
  if (value == null) return '-';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
};

const getCurrentMonth = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}`;
};

export default function Finance() {
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [month, setMonth] = useState(getCurrentMonth());
  const [tab, setTab] = useState(0);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const res = await API.get('/finance/summary', { params: { month } });
      setSummary(res.data);
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Erreur lors du chargement', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [month]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 6 },
        py: 5,
        background: 'var(--app-bg)',
      }}
    >
      <Box
        sx={{
          mb: 5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: 'var(--text-primary)',
            letterSpacing: 1,
            fontSize: { xs: 28, sm: 36, md: 42 },
          }}
        >
          Finance &amp; Inventaire
        </Typography>
        <Typography sx={{ color: 'var(--text-muted)', mt: 1, maxWidth: 520 }}>
          Vue mensuelle du chiffre d&apos;affaires, du profit et de la valeur du stock.
        </Typography>
      </Box>

      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 4,
          background: 'var(--surface-1)',
          border: '1px solid var(--border-subtle)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            type="month"
            label="Mois"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            size="small"
            sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: 180 }}
          />
          <Tabs
            value={tab}
            onChange={(e, newValue) => setTab(newValue)}
            textColor="inherit"
            indicatorColor="secondary"
            sx={{
              flex: 1,
              '& .MuiTab-root': { color: 'var(--text-muted)', fontWeight: 700 },
              '& .Mui-selected': { color: 'var(--text-primary)' },
            }}
          >
            <Tab label="Vue generale" />
            <Tab label="Fournisseurs" />
          </Tabs>
        </Stack>
        <Typography sx={{ color: 'var(--text-muted)', fontWeight: 700, mt: 2 }}>
          {summary?.profitNote || ''}
        </Typography>
      </Paper>

      {loading ? (
        <Stack alignItems="center">
          <CircularProgress sx={{ color: 'var(--text-primary)' }} />
        </Stack>
      ) : (
        <>
          {tab === 0 && (
            <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
            {[
              {
                label: 'Chiffre d\'affaires (mois)',
                value: formatCurrency(summary?.monthlyRevenue),
                icon: <TrendingUpIcon />,
                accent: '#22c55e',
              },
              {
                label: 'Profit brut (mois)',
                value: formatCurrency(summary?.monthlyProfit),
                icon: <SavingsIcon />,
                accent: '#f97316',
              },
              {
                label: 'Depenses (mois)',
                value: formatCurrency(summary?.monthlyExpenses),
                icon: <ReceiptLongIcon />,
                accent: '#ef4444',
              },
              {
                label: 'Profit net (mois)',
                value: formatCurrency(summary?.netProfit),
                icon: <AccountBalanceWalletIcon />,
                accent: '#38bdf8',
              },
            ].map((item) => (
              <Paper
                key={item.label}
                sx={{
                  flex: 1,
                  p: 3,
                  borderRadius: 4,
                  background:
                    'var(--surface-1)',
                  border: '1px solid var(--border-subtle)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `${item.accent}22`,
                    color: item.accent,
                    '& svg': { fontSize: 22 },
                  }}
                >
                  {item.icon}
                </Box>
                <Box>
                  <Typography sx={{ color: 'var(--text-muted)', fontWeight: 700 }}>
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'var(--text-primary)',
                      fontWeight: 800,
                      fontSize: isMobile ? 20 : 24,
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Stack>

          <Paper
            sx={{
              p: 3,
              mb: 2,
              borderRadius: 4,
              background: 'var(--surface-1)',
              border: '1px solid var(--border-subtle)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(56,189,248,0.2)',
                  color: '#38bdf8',
                }}
              >
                <InventoryIcon />
              </Box>
              <Box>
                <Typography sx={{ color: 'var(--text-muted)', fontWeight: 700 }}>
                  Valeur inventaire
                </Typography>
                <Typography sx={{ color: 'var(--text-primary)', fontWeight: 800, fontSize: isMobile ? 20 : 24 }}>
                  {formatCurrency(summary?.inventoryValue)}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper
            sx={{
              p: 3,
              borderRadius: 4,
              background: 'var(--surface-1)',
              border: '1px solid var(--border-subtle)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 2 }}>
              Depenses par categorie
            </Typography>
            {(summary?.expensesByCategory || []).length === 0 ? (
              <Typography sx={{ color: 'var(--text-muted)' }}>
                Aucune depense pour ce mois.
              </Typography>
            ) : (
              <Stack spacing={1.2}>
                {summary.expensesByCategory.map((item) => (
                  <Stack
                    key={item.category}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      background: 'var(--surface-3)',
                      border: '1px solid var(--border-subtle)',
                    }}
                  >
                    <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                      {item.category}
                    </Typography>
                    <Typography sx={{ color: 'var(--text-secondary)', fontWeight: 700 }}>
                      {formatCurrency(item.total)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
            </>
          )}

          {tab === 1 && (
            <Paper
              sx={{
                p: 3,
                borderRadius: 4,
                background: 'var(--surface-1)',
                border: '1px solid var(--border-subtle)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 2 }}>
                Resultats par fournisseur (mois)
              </Typography>
              <Box sx={{ mb: 3 }}>
                {(summary?.supplierStats || []).length === 0 ? (
                  <Typography sx={{ color: 'var(--text-muted)' }}>
                    Aucune vente pour ce mois.
                  </Typography>
                ) : (
                  <Stack spacing={1.2}>
                    {(() => {
                      const rows = summary?.supplierStats || [];
                      const maxRevenue = Math.max(...rows.map((r) => Number(r.revenue) || 0), 1);
                      return rows.map((row) => (
                        <Box
                          key={`bar-${row.supplier_id || row.supplier_name}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                          }}
                        >
                          <Typography
                            sx={{
                              color: 'var(--text-secondary)',
                              minWidth: 140,
                              fontWeight: 600,
                            }}
                          >
                            {row.supplier_name || 'Sans fournisseur'}
                          </Typography>
                          <Box
                            sx={{
                              flex: 1,
                              height: 10,
                              borderRadius: 999,
                              background: 'var(--surface-3)',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                height: '100%',
                                width: `${((Number(row.revenue) || 0) / maxRevenue) * 100}%`,
                                background: 'linear-gradient(90deg, #22c55e, #0ea5e9)',
                              }}
                            />
                          </Box>
                          <Typography sx={{ color: 'var(--text-secondary)', minWidth: 90 }}>
                            {formatCurrency(row.revenue)}
                          </Typography>
                        </Box>
                      ));
                    })()}
                  </Stack>
                )}
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Fournisseur', 'Umsatz', 'Gewinn', 'Quantite vendue'].map((label) => (
                        <TableCell
                          key={label}
                          sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 700,
                            backgroundColor: 'var(--table-header-bg)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(summary?.supplierStats || []).map((row) => (
                      <TableRow key={row.supplier_id || row.supplier_name}>
                        <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                          {row.supplier_name || 'Sans fournisseur'}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {formatCurrency(row.revenue)}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {formatCurrency(row.profit)}
                        </TableCell>
                        <TableCell sx={{ color: 'var(--text-secondary)' }}>
                          {row.soldQuantity}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}





